import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import ProductIcon from "@/components/ProductIcon";


export default function Shop() {
  return (
    <div className="min-h-screen bg-background text-foreground pt-32 pb-24" data-testid="shop-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="grid md:grid-cols-12 gap-8 items-end mb-20 md:mb-32"
        >
          <div className="md:col-span-8">
            <p className="font-script text-accent text-3xl mb-3">the pantry</p>
            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter" data-testid="shop-title">
              Our favourites.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="font-serif-alt italic text-xl text-foreground/70 border-l-2 border-accent pl-6 leading-relaxed">
              The bottles, jars, and boxes we&rsquo;d walk across town for. No sponsorships — just the pantry we actually keep.
            </p>
          </div>
        </motion.div>

        {/* Product grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 md:mt-32 border-t border-border pt-10 max-w-3xl"
        >
          <p className="font-serif-alt italic text-foreground/60 text-sm leading-relaxed">
            Some links go to Amazon search results so you can compare listings and sellers. We don&rsquo;t currently earn commission — this is a straight recommendation, not a storefront.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function ProductCard({ product, index }) {
  return (
    <motion.a
      href={product.link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      className="group relative flex flex-col overflow-hidden border border-border bg-secondary/40 hover:bg-secondary/70 transition-colors duration-500"
    >
      {/* Icon block — clean, editorial, outline-only */}
      <div
        className="relative aspect-[4/5] p-8 flex flex-col justify-between overflow-hidden bg-secondary/50 border-b border-border"
      >
        <div className="relative z-10 flex items-center justify-between text-foreground/60">
          <span className="font-serif-alt italic text-xs uppercase tracking-widest">{product.origin}</span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ProductIcon
            name={product.icon}
            className="w-24 h-24 md:w-28 md:h-28 text-accent/80"
            strokeWidth={1.1}
          />
        </div>
        <div className="relative z-10 text-foreground">
          <p className="font-script text-2xl md:text-3xl mb-2 text-accent">{product.brand}</p>
          <p className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-tighter">
            {product.name}
          </p>
        </div>
      </div>

      {/* Text block */}
      <div className="p-6 md:p-7 flex-1 flex flex-col justify-between gap-4">
        <div>
          <p className="font-serif-alt italic text-lg md:text-xl text-foreground leading-snug mb-3">
            {product.tagline}
          </p>
          <p className="font-serif-alt text-sm text-foreground/70 leading-relaxed">
            {product.note}
          </p>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-border/50">
          <span className="font-serif-alt italic text-xs uppercase tracking-widest text-foreground/50">
            {product.used_in
              ? `used in ${product.used_in.replaceAll("-", " ")}`
              : product.upcoming
                ? "for upcoming recipes"
                : "pantry staple"}
          </span>
          <span className="inline-flex items-center gap-1.5 font-serif-alt text-sm text-accent group-hover:gap-2.5 transition-all duration-300">
            Find it
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
          </span>
        </div>
      </div>
    </motion.a>
  );
}
